import cn from "@/utils/cn";
import Link, { LinkProps } from "next/link";
import { BlogPostDocumentData } from "../../../../../prismicio-types";
import {
  DateField,
  PrismicDocumentHeader,
  RichTextField,
} from "@prismicio/client";
import { PrismicImage, PrismicRichText } from "@prismicio/react";

type DocumentHeader = PrismicDocumentHeader<"blog_post", string>;

type BlogCardProps = {
  coverImage: BlogPostDocumentData["hero_image"];
  blogTitle: RichTextField;
  publishDate: DocumentHeader["last_publication_date"];
  blogLink: DocumentHeader["url"];
  blogExcerpt: RichTextField;
  blogDate: DateField;
} & React.HTMLAttributes<LinkProps>;

export default function BlogCard({
  coverImage,
  blogTitle,
  publishDate,
  blogLink,
  blogExcerpt,
  blogDate,
  className,
  ...props
}: BlogCardProps) {
  // Convert publishDate to a Date, then make it write like "January 22, 2024";
  const formattedDate = new Date(blogDate || publishDate).toLocaleDateString(
    "en-US",
    {
      month: "long",
      day: "numeric",
      year: "numeric",
    },
  );

  const transformText = (text: any, truncateLength = 100) => {
    if (text.length > truncateLength) {
      return text.substring(0, truncateLength) + "...";
    }
    return text;
  };
  return (
    // @ts-expect-error TODO: I'm sure this has some solution, but later.
    <Link
      href={blogLink || "/#"}
      className={cn(
        "border border-[#F6F6F6] w-[234px] h-[292.8px] md:w-[211.2px] md:h-[292.8px] xl:w-[352px] xl:h-[488px] bg-[#FDFDFD] rounded-[15px] group flex pb-4d flex-col items-center text-center shadow-sm hover:border hover:border-kd-lte-blue",
        className,
      )}
      {...props}
    >
      <div className=" relative h-[114px] min-h-[35%] w-full xl:h-[191px]">
        <PrismicImage
          field={coverImage}
          alt=""
          className="size-full rounded-t-[15px] object-cover"
        />
        <div className="absolute inset-0 hidden rounded-t-[15px] bg-kd-lte-blue opacity-30 group-hover:block"></div>
      </div>

      <div className="mt-4 flex size-full flex-col justify-between pb-[10px] xl:mt-[25px]">
        <div className="grow px-[13px] xl:px-[20px]">
          <PrismicRichText
            field={blogTitle}
            components={{
              paragraph: ({ children, text }) => (
                <h1
                  // @ts-expect-error TODO: I'm sure this has some solution, but this seems to be working for now.
                  title={children}
                  className="line-clamp-2 min-h-[24px] text-center text-[12px] font-semibold leading-[16.8px] text-kd-secondary-2 group-hover:text-kd-lte-blue xl:min-h-[46px] xl:text-[18px] xl:leading-[28px]"
                >
                  {transformText(text, 50)}
                </h1>
              ),
            }}
          />
          <PrismicRichText
            field={blogExcerpt}
            components={{
              paragraph: ({ children, text }) => (
                <p className="mt-1 line-clamp-5 text-[8.4px] leading-[15px] text-kd-secondary-3 md:mt-1 xl:mt-2 xl:text-sm  xl:leading-[25px]">
                  {transformText(text, 190)}
                </p>
              ),
            }}
          />
        </div>
        <div className="mt-1 flex flex-col items-center space-y-1.5 md:space-y-[3px] xl:space-y-2">
          <div
            className="text-[9.6px] font-semibold leading-[18.90px] text-kd-lte-blue group-hover:text-kd-lte-blue xl:text-base xl:leading-6"
            style={{
              textUnderlineOffset: "10px",
            }}
          >
            Read More
          </div>
          {/* <div className="mb-2  h-[1px] w-full bg-kd-secondary-6 md:mb-[6px] xl:mb-[22px]"></div> */}
          <div className="h-px w-full border-b border-kd-secondary-6"></div>
          <div className="mb-[6px] w-full text-[7.8px] font-medium leading-[18.90px] text-[#D1D1D6] md:mb-[2px] xl:mb-[6px] xl:text-[13px] xl:leading-6">
            {formattedDate}
          </div>
        </div>
      </div>
    </Link>
  );
}
